import React from 'react';
import {
  Card,
  Form,
  Button,
  Input,
  Select,
  Row,
  Col,
  Icon,
  Tooltip,
  Checkbox,
  message as showMessage,
} from 'antd';

import { postData, getData } from '../../helper/dataService';
import API from '../../helper/api';
import Spinner from '../common/Spinner';
import { delayApiRequest } from '../../helper/utility';

const ENGLISH_REGEX = /^[A-Za-z0-9\s!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]*$/;
const DIGIT_REGEX = /^[0-9]*$/;

class AssetAdd extends React.Component {
  state = {
    deviceFetching: false,
    deviceData: [],
    assetFetching: false,
    assetData: [],
    organizationFetching: false,
    organizationData: [],
    submitted: false,
    plansFetching: false,
    plansData: [],
    preinstallValue: '',
    preinstallSearching: false,
    preinstallId: null,
  };

  handlePreinstallSearch = (e) => {
    if (e) e.preventDefault();
    const { preinstallValue } = this.state;
    if (!preinstallValue) {
      return showMessage.warning('Please enter a search number');
    }

    this.setState({ preinstallSearching: true });
    getData(API.asset.preinstall(preinstallValue))
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          const {
            asset_type,
            chassis_no,
            contact_no,
            device_sim_number,
            engine_no,
            engine_sensor,
            installation_area,
            registration_no,
            relay_sensor,
            plan_price,
            device_imei,
          } = data;

          // Find plan by price if possible
          let matchedPlanOid = undefined;
          if (plan_price && this.state.plansData.length > 0) {
            const matchedPlan = this.state.plansData.find(
              (p) => Number(p.price) === Number(plan_price),
            );
            if (matchedPlan) {
              matchedPlanOid = matchedPlan._id.$oid;
            }
          }

          // Auto search and fill Device
          if (device_imei) {
            this.setState({ deviceFetching: true });
            getData(API.device.search(device_imei))
              .then(
                ({ data: { data: deviceResults, status: deviceStatus } }) => {
                  if (deviceStatus === 'success' && deviceResults.length > 0) {
                    const matchedDevice =
                      deviceResults.find((d) => d.imei === device_imei) ||
                      deviceResults[0];

                    this.setState({ deviceData: deviceResults });
                    this.props.form.setFieldsValue({
                      device_oid: matchedDevice._id.$oid,
                    });
                  }
                  this.setState({ deviceFetching: false });
                },
              )
              .catch(() => {
                this.setState({ deviceFetching: false });
              });
          }

          this.props.form.setFieldsValue({
            registration_number: registration_no || '',
            device_sim_number: device_sim_number || '',
            asset_type: asset_type || '',
            chassis_number: chassis_no || '',
            contact_no: contact_no || '',
            engine_number: engine_no || '',
            engine_sensor: !!engine_sensor,
            installation_area:
              installation_area && ENGLISH_REGEX.test(installation_area)
                ? installation_area
                : '',
            relay: !!relay_sensor,
            plan_oid: matchedPlanOid,
          });
          this.setState({ preinstallId: preinstallValue });
          showMessage.success('Data fetched successfully');
        } else {
          showMessage.error('Sorry! No data found');
        }
        this.setState({ preinstallSearching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ preinstallSearching: false });
      });
  };

  componentDidMount() {
    this.fetchPlans();
  }

  // * ASSET
  handleFetchAsset = (assetName) => {
    if (assetName.length >= 4) {
      delayApiRequest(() => this.fetchAsset(assetName));
    }
  };
  fetchAsset = (assetName) => {
    this.props.form.resetFields(['asset_oid']);
    this.setState({ assetData: [], assetFetching: true });

    getData(API.asset.search(assetName))
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          this.setState({ assetData: data });
        }
        this.setState({ assetFetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ assetFetching: false });
      });
  };

  clearAsset = () => {
    this.setState({
      assetData: [],
      deviceData: [],
      organizationData: [],
      preinstallId: null,
    });
    this.props.form.resetFields([
      'asset_oid',
      'device_oid',
      'organization_oid',
      'remarks',
    ]);
  };

  // * Organization
  handleFetchOrganization = (organizationName) => {
    if (organizationName.length >= 4) {
      delayApiRequest(() => this.fetchOrganization(organizationName));
    }
  };

  fetchOrganization = (organizationName) => {
    this.props.form.resetFields(['organization_oid']);

    this.setState({ organizationData: [], organizationFetching: true });
    getData(API.organization.search(organizationName))
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          this.setState({ organizationData: data });
        } else {
          showMessage.error('Sorry! Something went wrong');
        }
        this.setState({ organizationFetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ organizationFetching: false });
      });
  };

  clearOrganization = () => {
    this.setState({ organizationData: [], preinstallId: null });
    this.props.form.resetFields(['organization_oid']);
  };

  //* Device
  handleFetchDevice = (deviceName) => {
    if (deviceName.length >= 4) {
      delayApiRequest(() => this.fetchDevice(deviceName));
    }
  };

  fetchDevice = (deviceName) => {
    this.props.form.resetFields(['device_oid']);

    this.setState({ deviceData: [], deviceFetching: true });
    getData(API.device.search(deviceName))
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          this.setState({ deviceData: data });
        } else {
          showMessage.error('Sorry! Something went wrong');
        }
        this.setState({ deviceFetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ deviceFetching: false });
      });
  };

  clearDevice = () => {
    this.setState({ deviceData: [] });
    this.props.form.resetFields(['device_oid']);
  };

  fetchPlans = () => {
    this.setState({ plansData: [], plansFetching: true });
    getData(API.asset.plans)
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          const sortedPlans = data.sort(
            (a, b) => Number(a.price) - Number(b.price),
          );
          this.setState({ plansData: sortedPlans });
        }
        this.setState({ plansFetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong while fetching plans');
        this.setState({ plansFetching: false });
      });
  };

  // * set remarks
  setRemarks = (value) => {
    const asset = this.state.assetData.find(
      (asset) => asset._id.$oid === value,
    );

    this.props.form.setFieldsValue({
      remarks: asset.remarks ? asset.remarks : '',
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.setState({ submitted: true });

        const { assetData } = this.state;
        const { asset_oid } = values;
        const selectedAsset = assetData.find(
          ({ _id }) => _id.$oid === asset_oid,
        );

        postData(API.asset.add, {
          ...values,
          name: selectedAsset ? selectedAsset.name : '',
          preinstall_id: this.state.preinstallId || null,
        })
          .then(({ data: { status, display } }) => {
            if (status === 'success') {
              showMessage.success(display);
              this.props.form.resetFields([
                'asset_oid',
                'organization_oid',
                'device_oid',
                'device_sim_number',
                'registration_number',
                'asset_type',
                'chassis_number',
                'engine_number',
                'contact_no',
                'installation_area',
                'engine_sensor',
                'relay',
                'plan_oid',
                'remarks',
              ]);
              this.setState({
                assetData: [],
                deviceData: [],
                organizationData: [],
                preinstallId: null,
                preinstallValue: '',
              });
            } else if (status === 'failed') {
              showMessage.error(display);
            }
            this.setState({ submitted: false });
          })
          .catch(() => {
            showMessage.error('Sorry! Something went wrong');
            this.setState({ submitted: false });
          });
      }
    });
  };

  render() {
    const { getFieldDecorator, getFieldValue } = this.props.form;
    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
      },
    };
    const {
      assetFetching,
      assetData,
      deviceFetching,
      deviceData,
      organizationFetching,
      organizationData,
      submitted,
      plansFetching,
      plansData,
    } = this.state;

    const currentSelectedAsset = assetData.find(
      ({ _id }) => _id.$oid === getFieldValue('asset_oid'),
    );
    const currentSelectedDevice = deviceData.find(
      ({ _id }) => _id.$oid === getFieldValue('device_oid'),
    );
    const currentSelectedOrganization = organizationData.find(
      ({ _id }) => _id.$oid === getFieldValue('organization_oid'),
    );

    const isSearchDisabled =
      !getFieldValue('organization_oid') || !getFieldValue('asset_oid');

    return (
      <Row type='flex' justify='space-around' align='middle'>
        <Col xs={24} sm={18}>
          <Card
            title={
              <span className='title'>
                <Icon type='car' />
                {' Add Asset'}
              </span>
            }
            extra={
              <Tooltip
                title={
                  isSearchDisabled
                    ? 'Please select Organization, Asset and Device first'
                    : ''
                }
              >
                <Input.Search
                  placeholder='Search number...'
                  enterButton='Search'
                  value={this.state.preinstallValue}
                  onChange={(e) =>
                    this.setState({ preinstallValue: e.target.value })
                  }
                  onSearch={() => this.handlePreinstallSearch()}
                  loading={this.state.preinstallSearching}
                  disabled={isSearchDisabled}
                  style={{ width: 300 }}
                />
              </Tooltip>
            }
          >
            <Form onSubmit={this.handleSubmit} layout='vertical'>
              <Form.Item
                label='Organization Name'
                extra={
                  currentSelectedOrganization ? (
                    <Button.Group size='small'>
                      <Button>
                        {currentSelectedOrganization.name} [
                        {currentSelectedOrganization.organization_id}]
                      </Button>
                      <Tooltip title='Remove Organization'>
                        <Button
                          icon='delete'
                          onClick={this.clearOrganization}
                        />
                      </Tooltip>
                    </Button.Group>
                  ) : null
                }
                {...formItemLayout}
              >
                {getFieldDecorator('organization_oid', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select an organization',
                    },
                  ],
                })(
                  <Select
                    showSearch
                    placeholder='Search and Select Organization'
                    filterOption={false}
                    notFoundContent={
                      organizationFetching ? <Spinner /> : 'No Content Found'
                    }
                    onSearch={this.handleFetchOrganization}
                    defaultActiveFirstOption={false}
                  >
                    {organizationData.length > 0 &&
                      organizationData.map(({ _id, name, organization_id }) => (
                        <Select.Option key={_id.$oid}>
                          {name} [{organization_id}]
                        </Select.Option>
                      ))}
                  </Select>,
                )}
              </Form.Item>

              <Form.Item
                label='Asset Name'
                extra={
                  currentSelectedAsset ? (
                    <Button.Group size='small'>
                      <Button>
                        {currentSelectedAsset.name} [
                        {currentSelectedAsset.asset_id}]
                      </Button>
                      <Tooltip title='Remove Asset'>
                        <Button icon='delete' onClick={this.clearAsset} />
                      </Tooltip>
                    </Button.Group>
                  ) : null
                }
                {...formItemLayout}
              >
                {getFieldDecorator('asset_oid', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select a asset',
                    },
                  ],
                })(
                  <Select
                    showSearch
                    placeholder='Search and Select Asset'
                    filterOption={false}
                    notFoundContent={
                      assetFetching ? <Spinner /> : 'No Content Found'
                    }
                    onSearch={this.handleFetchAsset}
                    defaultActiveFirstOption={false}
                    onSelect={this.setRemarks}
                  >
                    {assetData.length > 0 &&
                      assetData.map(({ _id, name, asset_id }) => (
                        <Select.Option key={_id.$oid}>
                          {name} [{asset_id}]
                        </Select.Option>
                      ))}
                  </Select>,
                )}
              </Form.Item>

              <Form.Item
                label='Device'
                extra={
                  currentSelectedDevice ? (
                    <Button.Group size='small'>
                      <Button>
                        {currentSelectedDevice.imei} [
                        {currentSelectedDevice.device_id}] [
                        {currentSelectedDevice.device_type}]
                      </Button>
                      <Tooltip title='Remove device'>
                        <Button icon='delete' onClick={this.clearDevice} />
                      </Tooltip>
                    </Button.Group>
                  ) : null
                }
                {...formItemLayout}
              >
                {getFieldDecorator('device_oid', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select a device',
                    },
                  ],
                })(
                  <Select
                    showSearch
                    placeholder='Search and Select Device'
                    filterOption={false}
                    notFoundContent={
                      deviceFetching ? <Spinner /> : 'No Content Found'
                    }
                    onSearch={this.handleFetchDevice}
                    defaultActiveFirstOption={false}
                  >
                    {deviceData.length > 0 &&
                      deviceData.map(
                        ({ _id, imei, device_id, device_type }) => (
                          <Select.Option key={_id.$oid}>
                            {imei} [{device_id}] [{device_type}]
                          </Select.Option>
                        ),
                      )}
                  </Select>,
                )}
              </Form.Item>

              <Form.Item label='Device SIM Number' {...formItemLayout}>
                {getFieldDecorator('device_sim_number', {
                  rules: [
                    {
                      required: true,
                      message: 'Please input device SIM number',
                    },
                    {
                      pattern: DIGIT_REGEX,
                      message: 'Only numbers are allowed',
                    },
                  ],
                })(<Input placeholder='Device SIM Number' />)}
              </Form.Item>

              <Form.Item label='Registration Number' {...formItemLayout}>
                {getFieldDecorator('registration_number', {
                  rules: [
                    {
                      required: true,
                      message: 'Please input registration number',
                    },
                  ],
                })(<Input placeholder='Registration Number' />)}
              </Form.Item>

              <Form.Item label='Asset Type' {...formItemLayout}>
                {getFieldDecorator('asset_type', {
                  rules: [
                    {
                      required: true,
                      message: 'Please input asset type',
                    },
                  ],
                })(<Input placeholder='Asset Type' />)}
              </Form.Item>

              <Form.Item label='Chassis Number' {...formItemLayout}>
                {getFieldDecorator('chassis_number', {
                  initialValue: '',
                })(<Input placeholder='Chassis Number' />)}
              </Form.Item>

              <Form.Item label='Engine Number' {...formItemLayout}>
                {getFieldDecorator('engine_number', {
                  initialValue: '',
                })(<Input placeholder='Engine Number' />)}
              </Form.Item>

              <Form.Item label='Contact No' {...formItemLayout}>
                {getFieldDecorator('contact_no', {
                  initialValue: '',
                  rules: [
                    {
                      pattern: DIGIT_REGEX,
                      message: 'Only numbers are allowed',
                    },
                  ],
                })(<Input placeholder='Contact No' />)}
              </Form.Item>

              <Form.Item label='Installation Area' {...formItemLayout}>
                {getFieldDecorator('installation_area', {
                  initialValue: '',
                  rules: [
                    {
                      pattern: ENGLISH_REGEX,
                      message: 'Only English characters are allowed',
                    },
                  ],
                })(<Input placeholder='Installation Area' />)}
              </Form.Item>

              <Form.Item label='Engine Sensor' {...formItemLayout}>
                {getFieldDecorator('engine_sensor', {
                  valuePropName: 'checked',
                  initialValue: false,
                })(<Checkbox>Engine Sensor</Checkbox>)}
              </Form.Item>

              <Form.Item label='Relay' {...formItemLayout}>
                {getFieldDecorator('relay', {
                  valuePropName: 'checked',
                  initialValue: false,
                })(<Checkbox>Relay</Checkbox>)}
              </Form.Item>

              <Form.Item label='Select Plan' {...formItemLayout}>
                {getFieldDecorator('plan_oid', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select a plan',
                    },
                  ],
                })(
                  <Select placeholder='Select Plan' loading={plansFetching}>
                    {plansData.length > 0 &&
                      plansData.map(({ _id, plan_name, price, currency }) => (
                        <Select.Option key={_id.$oid}>
                          {plan_name} ({price} {currency})
                        </Select.Option>
                      ))}
                  </Select>,
                )}
              </Form.Item>
              <Form.Item label='Remarks' {...formItemLayout}>
                {getFieldDecorator('remarks', {
                  initialValue: '',
                  rules: [
                    {
                      max: 250,
                      message: 'The input not more than 250 characters.',
                    },
                  ],
                })(<Input.TextArea placeholder='Remarks' rows={4} />)}
              </Form.Item>
              <Button
                type='primary'
                size='large'
                htmlType='submit'
                style={{ float: 'right' }}
                loading={submitted}
              >
                Submit
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    );
  }
}

export default Form.create()(AssetAdd);
